'use client'
import { useRemoveCartMiniItem } from '@/hooks/react-query/cart/use-cart'
import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { useCampaignActive } from '@/providers/campaign-provider'
import { X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

interface Props {
  id: string
  name: string
  image: string
  regularPrice: number
  originalPrice: number
  variantTitle: string
  quantity: number
  link: string
  variantId: string
  indexId: string | null
  offPercent?: number
}

export const CartMiniItem = ({
  id,
  name,
  image,
  regularPrice,
  originalPrice,
  variantTitle,
  quantity,
  link,
  variantId,
  indexId,
  offPercent,
}: Props) => {
  const { mutateAsync: removeCartMiniItem, isPending: isRemovingCartMiniItem } =
    useRemoveCartMiniItem()

  const isCampaignActive = useCampaignActive('buy_more_save_more')

  const handleRemoveItem = async () => {
    await removeCartMiniItem({
      product_id: id,
      variants: variantId,
      box: '0',
      index_id: indexId,
      device_token: DeviceTokenHelper.get(),
    })
  }

  if (isRemovingCartMiniItem) {
    return <CartMiniItemSkeleton />
  }

  return (
    <div className='flex items-start gap-3'>
      <Link href={link}>
        <Image
          src={ImageHelper.getImageUrl(image, '')}
          alt='cart-item'
          width={100}
          height={120}
          className='aspect-[3/4] rounded-lg object-cover'
        />
      </Link>
      <div className='flex-1'>
        <div className='flex items-center justify-between gap-4'>
          <Link
            href={link}
            className='mb-0.5 line-clamp-2 text-sm font-medium text-neutral-900 hover:text-primary'
          >
            {name}
          </Link>

          <button
            onClick={handleRemoveItem}
            className='flex h-6 w-6 items-center justify-center'
          >
            <X
              size={16}
              className='h-4 w-4 text-neutral-500 hover:text-neutral-700'
            />
          </button>
        </div>
        <p className='text-xs text-neutral-900/50'>{variantTitle}</p>
        <div className='mt-3 flex items-center gap-2'>
          <p className='text-base font-medium leading-5.5 text-neutral-900'>
            {formatPrice(regularPrice)}
          </p>
          {originalPrice > regularPrice && (
            <del className='text-sm leading-4.5 text-neutral-500'>
              {formatPrice(originalPrice)}
            </del>
          )}
        </div>
        {offPercent && isCampaignActive ? (
          <i className='mt-2 text-sm text-secondary-500'>
            Khuyến mãi {offPercent}%
          </i>
        ) : null}
        <p className='mt-1 text-sm leading-4.5 text-neutral-800'>x{quantity}</p>
      </div>
    </div>
  )
}

// Skeleton component for cart mini item
export const CartMiniItemSkeleton = () => {
  return (
    <div className='flex items-center gap-3'>
      <div className='h-[120px] w-[100px] animate-pulse rounded-lg bg-neutral-200' />
      <div className='flex-1'>
        <div className='flex items-center justify-between gap-4'>
          <div className='flex-1'>
            <div className='mb-2 h-4 animate-pulse rounded bg-neutral-200' />
            <div className='h-3 w-3/4 animate-pulse rounded bg-neutral-200' />
          </div>
          <div className='h-6 w-6 animate-pulse rounded bg-neutral-200' />
        </div>
        <div className='mt-2 h-3 w-1/2 animate-pulse rounded bg-neutral-200' />
        <div className='mt-3 flex items-center gap-2'>
          <div className='h-4 w-16 animate-pulse rounded bg-neutral-200' />
          <div className='h-4 w-12 animate-pulse rounded bg-neutral-200' />
        </div>
        <div className='mt-1 h-4 w-8 animate-pulse rounded bg-neutral-200' />
      </div>
    </div>
  )
}
